<script>
    import { onMount, createEventDispatcher, onDestroy } from 'svelte';
    import Notification from '../notification/Notification.svelte';
    import { __, generateElementId, getActualKey, focusTrap, humanizeTime, dirComputed } from '@oat-sa-private/ui-core';
    import { wait, waitForEvent, getIsVolumeChangeSupported } from './utils';

    /**
     * @property {string} [src=''] Url to media content source
     * @property {string} [type=''] Mime type of media content
     * @property {Object[]} [tracks=[]] List of tracks for show subtitles. Each element in the array is mapped directly to a track element and any keys mapped directly to HTML attributes.
     * @property {boolean} [isLinear=false] Linear mode disables possibility to forward and backward
     * @property {string} [width='auto'] Player width
     * @property {string} [height='auto'] Player height. Has no effect on audio player.
     * @property {boolean} [allowFullscreen=true] Show full screen button for video content
     * @property {boolean} [allowPause=true] Show pause button during playing
     * @property {boolean} [deleteButton=false] Show delete button
     * @property {boolean} [loop=false] Auto restart media after it was ended
     * @property {boolean} [autostart=false] Autostart media after load (does not supported on Youtube content)
     * @property {number} [maxPlays=0] Amount of maximum plays. If there is disables possibility to forward and backward
     * @property {number} [plays=0] Amount of current plays
     * @property {boolean} [disabled=false] Set player to disabled state
     * @property {number} startTime Start time position of the player in seconds
     * @property {boolean} [allowVolumeControls=true] show volume controls
     * @property {boolean} [allowVolumeSlider=true] show volume slider
     * @property {string} feedbackLang Language for feedbacks and control labels
     * @property {string} dir ltr/rtl direction, should correspond to feedbackLang
     * @property {string[]} controlsOverride Array of control names to force
     * @property {number} [progressTimeoutMs=10000] Timeout in ms after which, if no progress was observed, player is considered stalled
     * @property {number} [loopRewindDelay=300] Timeout in ms given to the player to rewind to the beginning of the media
     * @property {string[]} [recoverableStatuses=[]] Player statuses for which the recovery message shall be shown
     *
     * @fires autostart {CustomEvent} Fires when video/audio started to play automatically on mounting.
     * @fires start {CustomEvent} Fires when video/audio started to play. Passes amount of current plays
     * @fires pause {CustomEvent} Fires when video/audio was paused. Passes amount of current plays
     * @fires finish {CustomEvent} Fires when video/audio was ended. Passes amount of current plays
     * @fires timeupdate {CustomEvent} Fires when video/audio time updates. Passes actual position
     * @fires error {CustomEvent} Fires when Plyr library can't load
     * @fires delete {CustomEvent} Fires when delete button is activated
     */
    export let src = '';
    export let type = '';
    export let tracks = [];
    export let isLinear = false;
    export let width = 'auto';
    export let height = 'auto';
    export let allowFullscreen = true;
    export let allowPause = true;
    export let loop = false;
    export let autostart = false;
    export let maxPlays = 0;
    export let plays = 0;
    export let disabled = false;
    export let startTime;
    export let allowVolumeControls = true;
    export let allowVolumeSlider = true;
    export let feedbackLang;
    export let dir;
    export let deleteButton = false;
    export let controlsOverride;
    export let progressTimeoutMs = 10000;
    export let loopRewindDelay = 300;
    export let recoverableStatuses = [];

    const dispatch = createEventDispatcher();
    const playerId = generateElementId('player');
    const feedbackId = generateElementId('feedback');

    const deleteIcon =
        '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13 1.00006H9C9 0.450061 8.55 6.10352e-05 8 6.10352e-05H6C5.45 6.10352e-05 5 0.450061 5 1.00006H1C0.45 1.00006 0 1.45006 0 2.00006V3.00006C0 3.55006 0.45 4.00006 1 4.00006H2V12.5001C2 13.3301 2.67 14.0001 3.5 14.0001H10.5C11.33 14.0001 12 13.3301 12 12.5001V4.00006H13C13.55 4.00006 14 3.55006 14 3.00006V2.00006C14 1.45006 13.55 1.00006 13 1.00006ZM11 12.5001C11 12.7801 10.78 13.0001 10.5 13.0001H9V9.00006H8V13.0001H6V9.00006H5V13.0001H3.5C3.22 13.0001 3 12.7801 3 12.5001V4.00006H11V12.5001ZM13 3.00006H12H2H1V2.00006H5H6V1.00006H8V2.00006H9H13V3.00006Z" fill="#121212"/></svg>';

    let player;
    let playerWrapperEl;
    let maxPlaysEl;
    let deleteButtonEl;

    let isPlayPauseShow;
    let willNewPlayStart = true;
    let isAutoStarted = false;
    let lastEndedEventTimestamp = null;
    let isVolumeChangeSupported = null;

    $: mode = getMode(type);
    $: provider = getProvider(type);
    $: if (mode === 'audio') {
        height = 'auto';
    }

    $: if (maxPlays) {
        updateMaxPlaysElement(plays, maxPlays);
    }

    $: player && setDisabledState(disabled);

    $: allowVolumeSlider = allowVolumeControls && allowVolumeSlider;

    /**
     * Set player to disable state
     * @param {Boolean} isDisabled
     */
    function setDisabledState(isDisabled) {
        if (isDisabled) {
            player.pause();
            player.elements.container.querySelectorAll('input, button').forEach(element => {
                element.setAttribute('disabled', true);
            });
        } else {
            player.elements.container.querySelectorAll('input, button').forEach(element => {
                element.removeAttribute('disabled');
            });
        }
    }

    /**
     * Detects and returns with player mode based on provider type
     * @param {string} mimeType mime type of media, like audio/mp3
     * @returns {string} - mode of player
     */
    function getMode(mimeType) {
        return mimeType && mimeType.startsWith('audio') ? 'audio' : 'video';
    }

    /**
     * Detects and returns with player provider based on provided type
     * @param {string} mimeType mime type of media, like audio/mp3
     * @returns {string} - provider of player
     */
    function getProvider(mimeType) {
        return mimeType && mimeType === 'video/youtube' ? 'youtube' : 'html5';
    }

    /**
     * Remove tabindex from player wrapper to prevent announcing redundant information about all controls and progress
     */
    function hidePlayerWrapperFromSR() {
        playerWrapperEl.querySelector('.plyr').removeAttribute('tabindex');
    }

    /**
     * Ensure the seek tooltip is not announced by screen readers
     */
    function hideSeekTooltipFromSR() {
        const progressEl = playerWrapperEl.querySelector('.plyr__progress');
        if (progressEl) {
            progressEl.querySelector('.plyr__tooltip').setAttribute('aria-hidden', true);
        }
    }

    /**
     * Play/Pause is the same button and should be display until playing if even pause is not allow
     */
    function updatePlayPauseButtonState() {
        isPlayPauseShow = allowPause || (player && !player.playing);
    }

    /**
     * Seek progressbar should be disabled when there is linear mode or max plays
     */
    function updateProgressInputState() {
        const seekEl = playerWrapperEl.querySelector('[data-plyr="seek"]');

        if (!seekEl) {
            return;
        }

        if (isLinear || maxPlays || !allowPause) {
            seekEl.setAttribute('disabled', 'true');
        } else {
            seekEl.removeAttribute('disabled');
        }
    }

    /**
     * Update text on overlaid button
     * @param {boolean} ended is video ended. It is necessary, because plyr video end detection doesn't work well
     * @returns {void}
     */
    function updatePlayLargeButton(ended = false) {
        const buttonCaptionEl = playerWrapperEl.querySelector('.plyr__control--overlaid[data-plyr="play"] span');

        if (!buttonCaptionEl) {
            return;
        }

        let buttonText = __('start playing');
        if (plays > 0) {
            buttonText = __('play again');
        }

        if (player.currentTime > 0 && !ended) {
            buttonText = __('resume playing');
        }
        buttonCaptionEl.innerHTML = buttonText;
    }

    /**
     * Define controls list for player
     * @returns {string[]} - array of controls names
     */
    function getPlayerControls() {
        if (Array.isArray(controlsOverride)) {
            return controlsOverride;
        }

        const controls = ['play', 'progress'];

        if (allowVolumeControls) {
            controls.push('mute');
        }

        if (allowVolumeSlider) {
            controls.push('volume');
        }

        if (allowFullscreen && mode === 'video') {
            controls.push('fullscreen');
        }

        if (mode === 'video') {
            controls.push('play-large');
        }

        if (tracks.length && mode === 'video' && provider === 'html5') {
            controls.push('settings');
        }

        return controls;
    }

    /**
     * Add custom controls block to show max plays info
     */
    function addMaxPlaysElement() {
        maxPlaysEl = document.createElement('div');
        maxPlaysEl.classList.add('plyr__controls__item');
        maxPlaysEl.classList.add('plyr__control');
        maxPlaysEl.classList.add('max-plays');

        const playBtn = playerWrapperEl.querySelector('[data-plyr="play"]');
        if (playBtn) {
            playBtn.after(maxPlaysEl);
            updateMaxPlaysElement(plays, maxPlays);
        }
    }

    /**
     * Update max plays info block
     * @param {number} playsAmount
     * @param {number} maxPlaysAmount
     */
    function updateMaxPlaysElement(playsAmount, maxPlaysAmount) {
        if (maxPlaysEl) {
            maxPlaysEl.innerHTML = `${__('Play')}: ${playsAmount} / ${maxPlaysAmount}`;
            maxPlaysEl.setAttribute('aria-label', __('Play: %d of %d', playsAmount, maxPlaysAmount));
        }
    }

    /**
     * Add "delete" button
     */
    function addDeleteButtonElement() {
        deleteButtonEl = document.createElement('button');
        deleteButtonEl.classList.add('plyr__controls__item');
        deleteButtonEl.classList.add('plyr__control');
        deleteButtonEl.classList.add('delete');
        deleteButtonEl.setAttribute('aria-label', __('delete'));
        deleteButtonEl.innerHTML = deleteIcon;

        playerWrapperEl.querySelector('[data-plyr="play"]').before(deleteButtonEl);
        deleteButtonEl.addEventListener('click', () => {
            dispatch('delete');
        });
    }

    /**
     * Media can be played
     * @param {number} playsAmount
     * @param {number} maxPlaysAmount
     * @param {boolean} ended is video ended. It is necessary, because plyr video end detection doesn't work well
     * @returns {boolean}
     */
    function canPlay(playsAmount, maxPlaysAmount, ended = false) {
        return (
            !disabled &&
            (!maxPlaysAmount ||
                playsAmount < maxPlaysAmount ||
                (playsAmount === maxPlaysAmount && !(player.currentTime === 0 || ended)))
        );
    }

    /**
     * Media can be seeking
     * @returns {boolean}
     */
    function canSeek() {
        return !disabled && !isLinear && !maxPlays;
    }

    /**
     * @param {Event} event
     */
    function onKeyDown(event) {
        const isLinearSeekingPrevented = preventLinearSeeking(event);
        const isTogglePlayPrevented = preventTogglePlay(event);

        if (!isLinearSeekingPrevented && !isTogglePlayPrevented) {
            dispatch(event.type, {
                pressedKey: event.key
            });
        }
    }

    /**
     * Stop propagate keydown event to prevent keyboard triggered seeking when it is not allowed
     * @param {Event} event
     * @returns {Boolean} - is prevented?
     */
    function preventLinearSeeking(event) {
        const pressedKey = getActualKey(event);
        if (!canSeek() && (pressedKey === 'left' || pressedKey === 'right')) {
            event.stopPropagation();
            return true;
        }
        return false;
    }

    /**
     * Prevent toggle play shortcut, when cannot play
     * @param {Event} event
     * @returns {Boolean} - is prevented?
     */
    function preventTogglePlay(event) {
        const pressedKey = getActualKey(event);
        if (!canPlay(plays, maxPlays) && pressedKey === 'k') {
            event.stopPropagation();
            return true;
        }
        return false;
    }

    /**
     * Define source for player
     */
    function setPlayerSource() {
        if (!src) {
            return;
        }

        player.source = {
            type: mode,
            sources: [
                {
                    src,
                    provider,
                    type
                }
            ],
            tracks
        };
    }

    /**
     * Set the currentTime property to the given time.
     * May only be called after Plyr's 'ready' event has fired.
     * TODO: Call player.currentTime on Plyr API, when bugs are fixed
     * @param {number} time
     */
    async function setPlayerCurrentTime(time) {
        // can be changed to currentTime, if https://github.com/sampotts/plyr/issues/1979 is fixed
        if (player.embed && player.embed.seekTo) {
            if (time) {
                player.embed.seekTo(time);
            }
        } else {
            // can be changed to currentTime, if https://github.com/sampotts/plyr/issues/1978 is fixed
            await waitForEvent(player.media, 'loadeddata' /* loadeddata should be waited because of safari */);
            if (time && player && player.media) {
                player.media.currentTime = time;
            }
        }

        await handleAutostart();
        setSeekListeners(time);
    }

    /**
     * Checks, whether player should and can autostarted
     *
     * @returns {boolean}
     */
    function shouldAutoStart() {
        return (
            autostart &&
            !disabled &&
            // youtube autostart causes invalid plays count, when autoplay is blocked by browser
            provider !== 'youtube' &&
            !player.playing
        );
    }

    /**
     * Checks and do autostart if necessary
     * @returns {Promise|void 0}
     */
    async function handleAutostart() {
        if (shouldAutoStart()) {
            isAutoStarted = true;

            return await player.play().finally(() => {
                isAutoStarted = false;
            });
        }
    }

    /**
     * As using 'seekTo' when we set startTime triggers meaningless seek-events
     * we try to distinguish then from caused-by-user-interaction events and not dispatch them.
     * Another not-caused-by-user seek-event happens because 'ended' handler calls 'stop()'
     * @param {number} [initialPlayTime]
     */
    async function setSeekListeners(initialPlayTime) {
        // Manually setting currentTime triggers seeked/seeking events
        // we avoid dispatching these events
        let mayAutoSeekHappen = typeof initialPlayTime === 'number';

        //on `ended`, `player.stop()` causes player to rewind to start and dispatch seeking(end->0)/seeked(end->0)
        const isAutoSeekAfterEnded = evt =>
            Date.now() - lastEndedEventTimestamp < 500 && evt.detail.plyr.currentTime < 0.05;

        player.on('seeked', event => {
            const isAutoSeekedEvent =
                (mayAutoSeekHappen && event.detail.plyr.currentTime - initialPlayTime < 0.05) ||
                isAutoSeekAfterEnded(event);
            if (!isAutoSeekedEvent) {
                dispatchPlayerEvent(event);
            }
            mayAutoSeekHappen = false;
        });
        player.on('seeking', event => {
            const isAutoSeekingEvent =
                (mayAutoSeekHappen && event.detail.plyr.currentTime === initialPlayTime) || isAutoSeekAfterEnded(event);
            if (!isAutoSeekingEvent) {
                dispatchPlayerEvent(event);
            }
        });

        // Auto-clear flag to enable logging user-triggered seek-events on the point = initialPlayTime
        // as if seeked-event won't happen, we'll keep ignoring user seek-events
        await wait(500);
        mayAutoSeekHappen = false;
    }

    /**
     * Detect video ended correctly
     * `player.ended` does not works correctly with youtube videos
     * currentTime can be bigger than duration in safari for youtube videos
     * @returns {Boolean}
     */
    function isEnded() {
        return player.ended || (provider === 'youtube' && player.embed.getDuration() <= player.embed.getCurrentTime());
    }

    /**
     * Generates the seek label from current time and duration. It output a human readable label with the durations.
     * @param {number} currentTime - current play time in seconds
     * @param {number} duration - total media duration in seconds
     * @returns {string} the seek label
     */
    function getSeekLabel(currentTime = 0, duration = 0) {
        return __('%s of %s', humanizeTime(currentTime), humanizeTime(duration));
    }

    // For tracking the player's status based on emitted events
    const playerStatuses = {
        idle: 'idle',
        loading: 'loading', // buffering
        playing: 'playing',
        paused: 'paused',
        stalled: 'stalled', // might not resume
        ended: 'ended',
        error: 'error'
    };

    let playerStatus = playerStatuses.idle;
    let readyForErrors = false;
    let lastProgressTimestamp = 0;

    // Tells if remediation notification should be shown
    $: needsReload = provider !== 'youtube' && recoverableStatuses && recoverableStatuses.includes(playerStatus);

    // Track the time passed since last progress
    const stallTracker = {
        timer: null,
        setTimer() {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                playerStatus = playerStatuses.stalled;
            }, progressTimeoutMs);
        },
        setOrContinueTimer() {
            if (!this.timer) {
                this.setTimer();
            }
        },
        clearTimer() {
            clearTimeout(this.timer);
            this.timer = null;
        }
    };

    /**
     * Define events for player
     */
    function setPlayerEvents() {
        player.once('ready', () => {
            // Error caught in the first moments needs special treatment (`handleInitialMediaError`)
            setTimeout(() => {
                readyForErrors = true;
            }, 500);

            //listen to timeupdate events only when ready
            player.on('timeupdate', () => {
                if (!Number.isNaN(player.currentTime)) {
                    lastProgressTimestamp = player.currentTime;
                    //Override the content of the seek "aria-valuetext" by hacking the translation
                    player.config.i18n.seekLabel = getSeekLabel(player.currentTime, player.duration);
                    dispatch('timeupdate', player.currentTime);

                    if (player.currentTime > 0) {
                        stallTracker.clearTimer();
                    }
                }

                if (player.playing) {
                    if (playerStatus !== playerStatuses.error && playerStatus !== playerStatuses.stalled) {
                        playerStatus = playerStatuses.playing;
                    }
                }

                // updates large button, after start time is set
                if (player.paused) {
                    updatePlayLargeButton();

                    if (playerStatus !== playerStatuses.error && playerStatus !== playerStatuses.stalled) {
                        playerStatus = playerStatuses.paused;
                    }
                }
            });

            hidePlayerWrapperFromSR();
            hideSeekTooltipFromSR();
            updatePlayPauseButtonState();
            updateProgressInputState();
            updatePlayLargeButton();

            if (maxPlays && maxPlays <= plays && !startTime) {
                disabled = true;
            }

            setDisabledState(disabled);

            if (maxPlays && !maxPlaysEl) {
                addMaxPlaysElement();
            }

            if (deleteButton && !deleteButtonEl) {
                addDeleteButtonElement();
            }

            player.elements.controls.addEventListener('keydown', onKeyDown);

            // make iframe not focusable
            const iframe = player.elements.container.querySelector('iframe');
            if (iframe) {
                iframe.setAttribute('tabindex', -1);
                iframe.setAttribute('aria-hidden', 'true');
            }

            player.config.i18n.seekLabel = getSeekLabel(player.currentTime, player.duration);

            setPlayerCurrentTime(startTime);
        });

        player.on('pause', event => {
            if (allowPause) {
                updatePlayLargeButton();
                if (playerStatus !== playerStatuses.error && playerStatus !== playerStatuses.stalled) {
                    playerStatus = playerStatuses.paused;
                }

                dispatch('pause', {
                    playsNumber: plays,
                    target: event.target,
                    position: event.detail && event.detail.plyr.currentTime
                });
            } else if (!player.seeking && !isEnded()) {
                player.play();
                event.stopPropagation();
            }
        });

        player.on('play', event => {
            updatePlayPauseButtonState();
            stallTracker.clearTimer();

            if (startTime) {
                startTime = null;
            } else if (willNewPlayStart) {
                // we can't check 'player.currentTime === 0' to count plays, because:
                // a) this handler fires *after* default event handler. In Chrome, sometimes, 'player.currentTime' is already > 0 here.
                // b) while youtube video is loading, user can click play/pause button or seek, but 'player.currentTime' stays 0 and 'plays' get incremented.
                plays++;
            }
            willNewPlayStart = false;

            dispatch('start', {
                playsNumber: plays,
                ...(isAutoStarted || { target: event.target }),
                autostart: isAutoStarted,
                position: event.detail && event.detail.plyr.currentTime
            });
        });

        player.on('ended', () => {
            willNewPlayStart = true;
            playerStatus = playerStatuses.ended;
            stallTracker.clearTimer();
            lastEndedEventTimestamp = Date.now();

            player.stop();

            updatePlayPauseButtonState();
            updatePlayLargeButton(true);

            dispatch('finish', {
                playsNumber: plays
            });

            if (loop && canPlay(plays, maxPlays, true)) {
                // give time to the player to rewind to the beginning of the media
                setTimeout(() => {
                    player.play();
                }, loopRewindDelay);
            }

            if (maxPlays && maxPlays <= plays) {
                if (player.fullscreen.active) {
                    player.fullscreen.exit();
                }

                disabled = true;
            }
        });

        player.on('progress', () => {
            // progress events can be fired even when currentTime did not advance
            if (player.currentTime > lastProgressTimestamp) {
                lastProgressTimestamp = player.currentTime;
                stallTracker.clearTimer();
                playerStatus = playerStatuses.playing;
            }
        });

        player.on('loadeddata', () => {
            if (autostart) {
                stallTracker.setOrContinueTimer();
            }
        });

        player.on('waiting', () => {
            // fires when insufficient data
            playerStatus = playerStatuses.loading;
            // begin timeout to stalled, cancellable by other events
            if (!player.paused) {
                stallTracker.setOrContinueTimer();
            }
        });

        player.on('stalled', () => {
            if (!player.paused) {
                stallTracker.clearTimer();
                playerStatus = playerStatuses.stalled;
            }
        });

        player.on('failed error', e => {
            if (readyForErrors) {
                stallTracker.clearTimer();
                playerStatus = playerStatuses.error;
                console.error(e); // eslint-disable-line no-console
            }
        });

        let focusTrapBehavior;

        player.on('enterfullscreen', () => {
            focusTrapBehavior = focusTrap(playerWrapperEl);
            // avoid screenreader could go behind the fullscreen
            playerWrapperEl.setAttribute('role', 'dialog');
        });

        player.on('exitfullscreen', () => {
            if (focusTrapBehavior) {
                focusTrapBehavior.destroy();
                focusTrapBehavior = null;
            }
            if (playerWrapperEl) {
                playerWrapperEl.removeAttribute('role');
            }
        });

        player.on('volumechange', () => {
            // if volume was set to 0 and user click to unmute
            if (player.config.muted && player.config.volume === 0) {
                player.volume = 0.5;
            }
        });
    }

    /**
     * Reload the player component, to try to recover from error/stalled statuses
     */
    function handleReload() {
        const lastTime = player.currentTime;
        const wasPlaying = player.playing;

        player.stop();

        playerStatus = null;
        stallTracker.setTimer();

        // try to make the browser retry media download now
        setPlayerSource();

        if (lastTime) {
            setPlayerCurrentTime(lastTime);
        }
        if (wasPlaying && !player.playing) {
            player.play();
        }
    }

    /**
     * Handle 'error' event on initial media element:
     * when setting `player.source` (`setPlayerSource`), Plyr temporarily sets empty src to media element;
     * this causes error which we don't want to log, so we catch it here
     * (other way to fix it is by setting `blankVideo` option in Plyr constructor)
     * @param {Event} e
     */
    function handleInitialMediaError(e) {
        /**
         * @type {MediaError} https://developer.mozilla.org/en-US/docs/Web/API/MediaError
         */
        const mediaError = e.target && e.target.error;
        if (mediaError && mediaError.code === 4 && !readyForErrors) {
            e.stopImmediatePropagation();
        }
    }

    /**
     * Load once in the document the plyr svg sprite
     * @param {string} spriteContent - the SVG or HTML content to attach
     */
    function attachIconSprite(spriteContent) {
        const spriteId = 'plyr-icon-sprite';
        if (!document.getElementById(spriteId)) {
            //load the SVG sprite into the page once
            const spriteElement = document.createElement('div');
            spriteElement.id = spriteId;
            spriteElement.setAttribute('hidden', '');
            spriteElement.innerHTML = spriteContent;
            document.body.insertAdjacentElement('afterbegin', spriteElement);
        }
    }

    /**
     * Re-dispatches player event,
     * encapsulating plyr object from Player consumers
     * @param {Event} event
     */
    function dispatchPlayerEvent(event) {
        dispatch(event.type, {
            position: event.detail && event.detail.plyr.currentTime,
            target: event.target
        });
    }

    onMount(() => {
        const isAllowCaptions = mode === 'video' && provider === 'html5';
        const initialCaptions = (isAllowCaptions && tracks.find(track => track.default)) || {};

        import('./Plyr.js')
            .then(result => {
                const Plyr = result.default;
                const plyrSvgSprite = result.plyrSvgSprite;
                const blankVideoUrl = result.blankVideoUrl;

                attachIconSprite(plyrSvgSprite);
                player = new Plyr(`#${playerId}`, {
                    controls: getPlayerControls(),
                    settings: ['captions'],
                    captions: { active: !!initialCaptions.default, language: initialCaptions.srclang },
                    storage: false,
                    // Youtube video is forced to 16:9 by default, but it should not be.
                    // 1:0 means 100*0/1 = 0, so it won't have padding-bottom, and height is set to 100% with css
                    ratio: provider === 'youtube' && height.includes('px') ? '1:0' : null,
                    i18n: {
                        play: __('play'),
                        pause: __('pause'),
                        mute: __('mute'),
                        unmute: __('unmute'),
                        enterFullscreen: __('enter fullscreen'),
                        exitFullscreen: __('exit fullscreen'),
                        volume: __('volume'),
                        seek: __('seek'),
                        seekLabel: getSeekLabel(0, 0),
                        buffered: __('buffered'),
                        frameTitle: __('Player for {title}'),
                        captions: __('Captions'),
                        settings: __('Settings'),
                        menuBack: __('Go back to previous menu'),
                        disabled: __('Disabled'),
                        enabled: __('Enabled')
                    },
                    listeners: {
                        seek: () => canSeek(),
                        play: () => canPlay(plays, maxPlays)
                    },
                    loadSprite: false,
                    blankVideo: blankVideoUrl
                });

                setPlayerSource();
                setPlayerEvents();

                if (feedbackLang) {
                    player.elements.controls.setAttribute('lang', feedbackLang);
                }

                dispatch('ready');
            })
            .catch(error => {
                dispatch('error', { error });
            });

        if (allowVolumeSlider) {
            getIsVolumeChangeSupported().then(result => {
                isVolumeChangeSupported = result;
            });
        }
    });

    onDestroy(() => {
        if (player && player.elements.controls) {
            player.elements.controls.removeEventListener('keydown', onKeyDown);
            player.destroy();
        }
        stallTracker.clearTimer();
    });
</script>

<style>
    .player {
        --plyr-control-icon-size: 2rem;
        --plyr-video-control-color: var(--color-text-default);
        --plyr-audio-control-color: var(--color-text-default);
        --plyr-video-control-color-hover: var(--color-text-default);
        --plyr-audio-control-color-hover: var(--color-text-default);
        --plyr-range-fill-background: var(--color-text-default);
        --plyr-control-spacing: 0;
        --plyr-range-thumb-height: 2rem;
        --plyr-control-toggle-checked-background: var(--color-text-default);

        --volume-slider-track-height: 0.5rem;
        --controls-size: 6rem;
        --play-pause-button-display: flex;
        --volume-slider-width: 18.5rem;
        --volume-input-display: block;

        max-width: 100%;

        &.disabled {
            pointer-events: none;

            --plyr-video-control-color: var(--color-text-disabled);
            --plyr-audio-control-color: var(--color-text-disabled);
            --plyr-video-control-color-hover: var(--color-text-disabled);
            --plyr-audio-control-color-hover: var(--color-text-disabled);
            --plyr-range-fill-background: var(--color-text-disabled);

            & :global(.plyr button.plyr__control--overlaid) {
                display: none;
            }

            & :global(.plyr .plyr__controls) {
                &,
                & > :global(*:not(:last-child)) {
                    border-color: var(--color-border-disabled);
                }
                & :global(button.plyr__control) {
                    &:hover {
                        background-color: var(--color-bg-disabled);
                    }
                }

                & :global(.plyr__volume input::-webkit-slider-thumb) {
                    border-color: var(--color-border-disabled);
                }

                & :global(.plyr__volume input::-moz-range-thumb) {
                    border-color: var(--color-border-disabled);
                }
            }
        }

        &.linear :global(.plyr .plyr__controls .plyr__progress__container input) {
            cursor: default;
        }

        @define-mixin sliderTrack {
            appearance: none;
            background-color: var(--color-gs-light-hover-bg);
        }

        @define-mixin sliderProgressThumb {
            appearance: none;
            border-radius: 0;
            width: 0;
            box-shadow: -100vw 0 0 100vw var(--color-gs-dark);

            background-color: var(--color-gs-dark);
        }

        @define-mixin sliderVolumeThumb {
            appearance: none;

            width: 2rem;
            height: 2rem;

            box-sizing: border-box;

            border: var(--border-medium) solid var(--color-border-default);
        }

        & :global(.plyr:not(.plyr--hide-controls) .plyr__controls:not(:empty) ~ .plyr__captions) {
            transform: translateY(calc(var(--controls-size) * -1));
        }

        & :global(.plyr iframe) {
            transition: opacity 0.3s, filter 0.3s;
        }

        /* hide youtube controls during pause */

        & :global(.plyr.plyr--paused iframe) {
            filter: blur(100vmax);
            opacity: 0;
        }

        & :global(.plyr) {
            height: 100%;

            & :global(.plyr__video-embed) {
                height: 100%;
            }
            & :global(button.plyr__control--overlaid) {
                height: var(--controls-size);

                display: flex;
                align-items: center;

                padding: 0 var(--space-2x);

                color: var(--color-text-inverted);

                background-color: var(--color-brand);

                border-radius: 3rem;
                border: var(--border-medium) solid var(--color-border-inverted);

                letter-spacing: inherit;
                word-spacing: inherit;

                & :global(span) {
                    height: auto !important;
                    width: auto !important;
                    position: relative !important;
                    display: block;

                    margin-left: var(--space-1x5);

                    text-transform: uppercase;
                    white-space: nowrap;
                }
            }

            & :global(button.plyr__control--overlaid:focus-visible) {
                outline: none;

                @add-mixin outline-focus-after -1rem;

                &:after {
                    border-radius: var(--radius-pill);
                }
            }

            & :global(.plyr__controls) {
                padding: 0;

                border: var(--border-medium) solid var(--color-border-default);

                & :global(.plyr__control) {
                    height: var(--controls-size);
                    width: var(--controls-size);

                    display: flex;
                    justify-content: center;
                    align-items: center;

                    border-radius: 0;

                    background-color: var(--color-bg-default);
                }

                & :global(.plyr__menu__container) {
                    right: 0;

                    & :global(.plyr__control) {
                        width: 100%;
                        justify-content: flex-end;

                        & :global(span:not(.plyr__badge)) {
                            padding: 0 var(--space-2x);
                        }
                    }

                    & :global(.plyr__control:focus-visible) {
                        outline: none;

                        & :global(span) {
                            @add-mixin outline-focus-after 0.25rem;
                        }
                    }

                    & :global(.plyr__control:after) {
                        right: 1rem;
                    }

                    & :global(.plyr__control[data-plyr='settings']) {
                        transform: none;
                    }

                    & :global(.plyr__control[data-plyr='language']) {
                        padding: var(--space-1x);
                    }

                    & :global(.plyr__control[data-plyr='language'] > span) {
                        padding-right: 0;
                    }

                    & :global(.plyr__control[data-plyr='language']:after) {
                        left: 13px;
                    }
                }

                & :global(.plyr__menu__container:after) {
                    content: none;
                }

                & :global(.plyr__control.max-plays) {
                    width: auto;

                    padding: var(--space-2x);

                    cursor: default;

                    text-transform: uppercase;

                    @add-mixin bold;
                }

                & :global(.plyr__control[data-plyr='play']) {
                    display: var(--play-pause-button-display);
                }

                & :global(button.plyr__control:hover) {
                    background-color: var(--color-bg-actionable-secondary-hover);
                }

                &
                    :global(
                        button:not([data-plyr='language']):not(.plyr__control--forward):not(
                                .plyr__control--back
                            ).plyr__control:focus-visible
                    ) {
                    outline: none;

                    @add-mixin outline-focus-after 0.25rem;
                }

                & :global(.plyr__volume) {
                    height: var(--controls-size);
                    width: var(--volume-slider-width);
                    min-width: var(--volume-slider-width);
                    background-color: var(--color-bg-default);

                    & :global(button) {
                        width: var(--controls-size);
                        height: var(--controls-size);
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }

                    & :global(input) {
                        display: var(--volume-input-display);
                        padding-right: var(--space-2x);
                        min-width: 0;
                    }

                    & :global(input::-webkit-slider-runnable-track) {
                        @add-mixin sliderTrack;

                        height: var(--volume-slider-track-height);
                    }

                    & :global(input::-moz-range-track) {
                        @add-mixin sliderTrack;

                        height: var(--volume-slider-track-height);
                    }

                    & :global(input::-webkit-slider-thumb) {
                        @add-mixin sliderVolumeThumb;
                    }

                    & :global(input::-moz-range-thumb) {
                        @add-mixin sliderVolumeThumb;
                    }

                    & :global(input:focus-visible::-webkit-slider-runnable-track) {
                        @add-mixin simple-outline var(--color-border-focus), 1.25rem;
                    }

                    & :global(input:focus-visible::-moz-range-track) {
                        @add-mixin simple-outline var(--color-border-focus), 1.25rem;
                    }
                }

                & :global(.plyr__progress__container) {
                    height: var(--controls-size);

                    background-color: var(--color-gs-light-hover-bg);

                    & :global(input) {
                        height: var(--controls-size);

                        overflow: hidden;
                        border-radius: 0;

                        cursor: pointer;

                        background-color: var(--color-gs-light-hover-bg);
                    }

                    & :global(input::-webkit-slider-thumb) {
                        @add-mixin sliderProgressThumb;
                        width: 1px;
                        margin-left: -1px;
                    }

                    & :global(input::-moz-range-thumb) {
                        @add-mixin sliderProgressThumb;
                    }

                    & :global(input::-webkit-slider-runnable-track) {
                        @add-mixin sliderTrack;

                        background: transparent;
                    }

                    & :global(input::-moz-range-track) {
                        @add-mixin sliderTrack;

                        background: transparent;
                    }

                    & :global(input:focus-visible::-webkit-slider-runnable-track) {
                        @add-mixin simple-outline var(--color-border-focus), 2rem;
                    }

                    & :global(input:focus-visible::-moz-range-track) {
                        @add-mixin simple-outline var(--color-border-focus), 2rem;
                    }
                }
            }

            & :global(.plyr__controls > *:not(:last-child)) {
                border-right: var(--border-medium) solid var(--color-border-default);
            }
        }
    }

    :global(.player[dir='rtl'] .plyr) {
        & :global(button.plyr__control--overlaid) {
            flex-direction: row-reverse;

            & :global(span) {
                margin-left: 0;
                margin-right: var(--space-1x5);
            }
        }

        & :global(.plyr__controls) {
            & :global(.plyr__volume) {
                flex-direction: row-reverse;

                padding-right: 0;

                & :global(input) {
                    direction: rtl;

                    padding-right: 0;
                    padding-left: var(--space-2x);
                }

                & :global(button svg) {
                    transform: scaleX(-1);
                }

                & :global(input::-webkit-slider-runnable-track) {
                    background-image: linear-gradient(
                        to left,
                        currentColor var(--value, 0),
                        transparent var(--value, 0)
                    );
                }
            }

            & :global(.plyr__menu__container .plyr__control[data-plyr='language']) {
                flex-direction: row-reverse;
            }

            & :global(.plyr__menu__container .plyr__control[data-plyr='language'] > span) {
                flex-direction: row-reverse;

                padding-right: var(--space-2x);
                padding-left: 0;
            }

            & :global(.plyr__menu__container .plyr__control--back span) {
                justify-content: flex-end;
            }

            & :global(.plyr__menu__container .plyr__control--back:after) {
                right: auto;
                left: 1rem;

                border-right-color: transparent;
                border-left-color: var(--plyr-menu-arrow-color);
            }

            & :global(.plyr__menu__container .plyr__control[data-plyr='language']:after) {
                right: 13px;
                left: auto;
            }

            & :global(button[data-plyr='fullscreen'] svg) {
                transform: scaleX(-1);
            }
        }
    }
</style>

{#if needsReload}
    <Notification
        title={__('Media error detected')}
        message={__('Please reload the media to try again')}
        hierarchy="warning"
        actions={[
            {
                type: 'button-link',
                href: '#',
                title: 'Reload',
                action: 'reload'
            }
        ]}
        on:notificationclick={handleReload} />
{/if}
<!-- captions add dynamically so warning should be disabled -->
<!-- svelte-ignore a11y-media-has-caption -->
<div
    class="player mode-{mode}"
    class:visually-hidden={needsReload}
    class:autostart
    class:disabled
    class:linear={isLinear || maxPlays}
    {dir}
    use:dirComputed
    bind:this={playerWrapperEl}
    style="
        width: {width};
        height: {height};
        --play-pause-button-display: {isPlayPauseShow ? 'flex' : 'none'};
        --volume-slider-width: {allowVolumeSlider && isVolumeChangeSupported ? '18.5rem' : 'auto'};
        --volume-input-display: {allowVolumeSlider && isVolumeChangeSupported ? 'block' : 'none'};
    "
    aria-labelledby={feedbackId}
    aria-disabled={disabled ? true : void 0}>
    {#if mode === 'audio'}
        <audio controls id={playerId} crossorigin="anonymous" on:error={handleInitialMediaError} />
    {:else}
        <video controls id={playerId} crossorigin="anonymous" on:error={handleInitialMediaError} />
    {/if}
</div>
<span id={feedbackId} class="visually-hidden" lang={feedbackLang}>
    {__('media player')}
</span>
